import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { despesasRecorrentes as despesasRecApi, despesasEsporadicas as despesasEspApi, meses as mesesApi } from '../lib/api';
import { useMonth } from '../context/MonthContext';
import { useAuth } from '../context/AuthContext';
import { canAddDespesa, getPlanLimits } from '../utils/planLimits';
import { UpgradePrompt, LimitBadge } from '../components/UpgradePrompt';
import { formatCurrency, parseCurrency, getMonthName, getPreviousMonthOptions } from '../utils/format';
import { CategorySelector } from '../components/CategorySelector';
import LoadingTimeout from '../components/LoadingTimeout';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { useRegisterRefresh } from '../context/RefreshContext';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Check,
  Receipt,
  Sparkles,
  Repeat,
  Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';

function DespesasPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan = user?.plan || 'gratuito';
  const { mesData, refreshData, currentMonth } = useMonth();

  const [despesasRec, setDespesasRec] = useState([]);
  const [despesasEsp, setDespesasEsp] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('recorrentes');
  const [copying, setCopying] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('recorrente'); // 'recorrente' or 'esporadica'
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [scopeDialog, setScopeDialog] = useState(null); // { data } — pending save waiting for scope choice

  // Optional "start in an earlier month" for new fixed items
  const [startEarlier, setStartEarlier] = useState(false);
  const [startMonth, setStartMonth] = useState(null); // { mes, ano }
  const prevMonthOptions = getPreviousMonthOptions(currentMonth);

  // Form state
  const [form, setForm] = useState({
    descricao: '',
    categoria: '',
    valor: '',
    data_vencimento: '1',
    data_pagamento: '',
    observacao: '',
    pago: false,
    is_fixo: true,
  });

  // Limit checks (computed after state is declared)
  const limits = getPlanLimits(plan);
  const totalDespesasCount = despesasRec.length + despesasEsp.length;
  const atDespesaLimit = !canAddDespesa(plan, totalDespesasCount);

  // Load data
  const loadData = useCallback(async () => {
    if (!mesData?.id) return;
    
    setLoading(true);
    try {
      const [recRes, espRes] = await Promise.all([
        despesasRecApi.list(mesData.id),
        despesasEspApi.list(mesData.id),
      ]);
      setDespesasRec(recRes);
      setDespesasEsp(espRes);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [mesData?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { timedOut, resetTimeout } = useLoadingTimeout(loading, loadData);
  useRegisterRefresh(loadData);

  // Calculate totals
  const totalRec = despesasRec.reduce((sum, d) => sum + d.valor, 0);
  const totalEsp = despesasEsp.reduce((sum, d) => sum + d.valor, 0);
  const totalGeral = totalRec + totalEsp;
  
  const pagasRec = despesasRec.filter(d => d.pago).length;
  const pagasEsp = despesasEsp.filter(d => d.pago).length;
  const totalPagas = pagasRec + pagasEsp;
  const totalDespesas = despesasRec.length + despesasEsp.length;

  // Open modal for new item
  const openNewModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setStartEarlier(false);
    setStartMonth(null);
    setForm({
      descricao: '',
      categoria: '',
      valor: '',
      data_vencimento: '1',
      data_pagamento: '',
      observacao: '',
      pago: false,
      is_fixo: type === 'recorrente',
    });
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (item, type) => {
    setModalType(type);
    setEditingItem(item);
    setForm({
      descricao: item.descricao,
      categoria: item.categoria,
      valor: item.valor.toString().replace('.', ','),
      data_vencimento: item.data_vencimento?.toString() || '1',
      data_pagamento: item.data_pagamento || '',
      observacao: item.observacao || '',
      pago: item.pago,
      is_fixo: item.is_fixo ?? false,
    });
    setShowModal(true);
  };

  // Save item
  const handleSave = async () => {
    if (!mesData?.id) return;

    const api = modalType === 'recorrente' ? despesasRecApi : despesasEspApi;

    const baseData = {
      mes_id: mesData.id,
      descricao: form.descricao || form.categoria,
      categoria: form.categoria,
      valor: parseCurrency(form.valor),
      pago: form.pago,
    };

    const data = modalType === 'recorrente'
      ? { ...baseData, data_vencimento: parseInt(form.data_vencimento) || 1, is_fixo: form.is_fixo }
      : { ...baseData, data_pagamento: form.data_pagamento, observacao: form.observacao };

    // Fixed recorrente being edited → ask scope before saving
    if (editingItem && modalType === 'recorrente' && form.is_fixo) {
      setShowModal(false);
      setScopeDialog({ data });
      return;
    }

    const createOpts =
      modalType === 'recorrente' && form.is_fixo && startEarlier && startMonth
        ? { startMes: startMonth.mes, startAno: startMonth.ano }
        : {};

    try {
      if (editingItem) {
        await api.update(editingItem.id, data);
      } else {
        await api.create(data, createOpts);
      }
      setShowModal(false);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  const handleScopeChoice = async (propagate) => {
    if (!scopeDialog || !editingItem) return;
    setScopeDialog(null);
    try {
      await despesasRecApi.update(editingItem.id, scopeDialog.data, { propagate });
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  // Toggle paid status
  const togglePago = async (item, type) => {
    const api = type === 'recorrente' ? despesasRecApi : despesasEspApi;
    try {
      await api.togglePago(item.id);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to toggle pago:', error);
    }
  };

  // Copy sporadic expenses from previous month
  const handleCopyEsporadicas = async () => {
    setCopying(true);
    try {
      await mesesApi.copiarDespesasEsporadicasAnterior(currentMonth.mes, currentMonth.ano);
      await loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to copy sporadic despesas:', error);
    } finally {
      setCopying(false);
    }
  };

  // Delete item
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    const api = deleteConfirm.type === 'recorrente' ? despesasRecApi : despesasEspApi;
    try {
      await api.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  // Render expense item
  const renderExpenseItem = (item, type) => {
    return (
      <div
        key={item.id}
        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border-2 transition-all ${
          item.pago
            ? 'bg-green-50 border-green-300'
            : 'bg-amber-50 border-amber-300'
        }`}
        data-testid={`despesa-item-${item.id}`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={() => togglePago(item, type)}
            className={`w-12 h-12 shrink-0 flex items-center justify-center rounded-xl border-3 transition-all ${
              item.pago
                ? 'bg-green-600 border-green-600 text-white'
                : 'bg-white border-amber-400 text-amber-500 hover:border-green-500'
            }`}
            aria-label={item.pago ? 'Marcar como pendente' : 'Marcar como pago'}
            data-testid={`toggle-pago-${item.id}`}
          >
            {item.pago && <Check size={28} strokeWidth={3} />}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-lg font-semibold text-[#1B2A4A] break-words">{item.descricao}</p>
              {item.is_fixo && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                  <Repeat size={12} />
                  Fixa
                </span>
              )}
            </div>
            <p className="text-base text-slate-500">
              {item.categoria}
              {type === 'recorrente' && item.data_vencimento && ` • Dia ${item.data_vencimento}`}
            </p>
            {item.observacao && (
              <p className="text-sm text-slate-400 italic break-words">{item.observacao}</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 pl-15 sm:pl-0">
          <span className={`text-xl font-bold tabular-nums ${item.pago ? 'text-green-700' : 'text-amber-700'}`}>
            {formatCurrency(item.valor)}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => openEditModal(item, type)}
              className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#1B2A4A] hover:bg-slate-200 rounded-lg"
              aria-label="Editar"
            >
              <Edit2 size={20} />
            </button>
            <button
              onClick={() => setDeleteConfirm({ type, id: item.id })}
              className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-100 rounded-lg"
              aria-label="Excluir"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        {timedOut ? (
          <LoadingTimeout
            timedOut={timedOut}
            onRetry={() => {
              resetTimeout();
              loadData();
            }}
          />
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1B2A4A] border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-slate-600">Carregando despesas...</p>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="despesas-page">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-2 border-slate-200" data-testid="total-recorrentes-card">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold text-slate-500">Recorrentes</p>
              <p className="text-xl font-bold text-[#1B2A4A] tabular-nums">{formatCurrency(totalRec)}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-slate-200" data-testid="total-esporadicas-card">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold text-slate-500">Esporádicas</p>
              <p className="text-xl font-bold text-[#1B2A4A] tabular-nums">{formatCurrency(totalEsp)}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-amber-300 bg-amber-50" data-testid="total-geral-card">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold text-amber-700">Total Despesas</p>
              <p className="text-xl font-bold text-amber-700 tabular-nums">{formatCurrency(totalGeral)}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-300 bg-green-50" data-testid="pagas-card">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold text-green-700">Pagas</p>
              <p className="text-xl font-bold text-green-700">{totalPagas} de {totalDespesas}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-14 bg-slate-100 rounded-xl p-1">
            <TabsTrigger 
              value="recorrentes" 
              className="h-12 text-base font-semibold rounded-lg data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"
              data-testid="tab-recorrentes"
            >
              Recorrentes ({despesasRec.length})
            </TabsTrigger>
            <TabsTrigger 
              value="esporadicas"
              className="h-12 text-base font-semibold rounded-lg data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"
              data-testid="tab-esporadicas"
            >
              Esporádicas ({despesasEsp.length})
            </TabsTrigger>
          </TabsList>

          {/* Recorrentes Tab */}
          <TabsContent value="recorrentes" className="mt-4">
            {atDespesaLimit && (
              <UpgradePrompt feature="adicionar mais despesas" />
            )}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg font-bold text-[#1B2A4A]">
                  Despesas Recorrentes
                  {limits.despesasPerMonth !== Infinity && (
                    <LimitBadge current={totalDespesasCount} max={limits.despesasPerMonth} />
                  )}
                </CardTitle>
                <Button
                  onClick={() => openNewModal('recorrente')}
                  disabled={atDespesaLimit}
                  className="h-12 w-full sm:w-auto px-4 bg-[#1B2A4A] hover:bg-[#2C3E50] rounded-xl disabled:opacity-50"
                  data-testid="add-recorrente-btn"
                >
                  <Plus size={20} className="mr-2" />
                  Adicionar
                </Button>
              </CardHeader>
              <CardContent>
                {despesasRec.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Receipt size={40} className="mx-auto mb-3 opacity-50" />
                    <p className="text-lg">Nenhuma despesa recorrente</p>
                    <p className="text-base">Adicione contas fixas como aluguel, energia, etc.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {despesasRec
                      .sort((a, b) => a.data_vencimento - b.data_vencimento)
                      .map(item => renderExpenseItem(item, 'recorrente'))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Esporádicas Tab */}
          <TabsContent value="esporadicas" className="mt-4">
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg font-bold text-[#1B2A4A]">
                  Despesas Esporádicas
                </CardTitle>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={handleCopyEsporadicas}
                    disabled={copying}
                    variant="outline"
                    className="h-12 flex-1 sm:flex-initial px-3 sm:px-4 border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white rounded-xl"
                    data-testid="copy-despesas-esporadicas-btn"
                  >
                    <Copy size={18} className="mr-2" />
                    <span className="hidden sm:inline">{copying ? 'Copiando...' : 'Copiar do mês anterior'}</span>
                    <span className="sm:hidden">{copying ? 'Copiando...' : 'Copiar'}</span>
                  </Button>
<Button
                    onClick={() => openNewModal('esporadica')}
                    disabled={atDespesaLimit}
                    className="h-12 flex-1 sm:flex-initial px-4 bg-[#1B2A4A] hover:bg-[#2C3E50] rounded-xl disabled:opacity-50"
                    data-testid="add-esporadica-btn"
                  >
                    <Plus size={20} className="mr-2" />
                    Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {despesasEsp.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Sparkles size={40} className="mx-auto mb-3 opacity-50" />
                    <p className="text-lg">Nenhuma despesa esporádica</p>
                    <p className="text-base">Adicione gastos variáveis como presentes, faxineira, etc.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {despesasEsp.map(item => renderExpenseItem(item, 'esporadica'))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="despesa-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1B2A4A]">
                {editingItem 
                  ? `Editar Despesa ${modalType === 'recorrente' ? 'Recorrente' : 'Esporádica'}`
                  : `Nova Despesa ${modalType === 'recorrente' ? 'Recorrente' : 'Esporádica'}`
                }
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                Preencha os dados da despesa
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Categoria</Label>
                <CategorySelector
                  tipo="despesa"
                  value={form.categoria}
                  onChange={(value) => setForm(prev => ({ ...prev, categoria: value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Descrição</Label>
                <Input
                  value={form.descricao}
                  onChange={(e) => setForm(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Ex: Aluguel POA, Energia Janeiro"
                  className="h-14 text-lg border-2"
                  data-testid="descricao-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Valor (R$)</Label>
                <Input
                  value={form.valor}
                  onChange={(e) => setForm(prev => ({ ...prev, valor: e.target.value }))}
                  placeholder="0,00"
                  className="h-14 text-lg border-2"
                  data-testid="valor-input"
                />
              </div>
              {modalType === 'recorrente' ? (
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Dia do Vencimento</Label>
                  <Select
                    value={form.data_vencimento}
                    onValueChange={(value) => setForm(prev => ({ ...prev, data_vencimento: value }))}
                  >
                    <SelectTrigger className="h-14 text-lg border-2" data-testid="vencimento-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()} className="text-lg">
                          Dia {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Data do Pagamento</Label>
                    <Input
                      type="date"
                      value={form.data_pagamento}
                      onChange={(e) => setForm(prev => ({ ...prev, data_pagamento: e.target.value }))}
                      className="h-14 text-lg border-2"
                      data-testid="data-pagamento-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Observação</Label>
                    <Textarea
                      value={form.observacao}
                      onChange={(e) => setForm(prev => ({ ...prev, observacao: e.target.value }))}
                      placeholder="Ex: Presente de aniversário da neta"
                      className="text-lg border-2 min-h-[80px]"
                      data-testid="observacao-input"
                    />
                  </div>
                </>
              )}
              {modalType === 'recorrente' && (
                <div className="flex items-center gap-3 pt-2">
                  <Checkbox
                    id="is-fixo"
                    checked={form.is_fixo}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_fixo: !!checked }))}
                    className="w-8 h-8 border-2"
                    data-testid="is-fixo-checkbox"
                  />
                  <Label htmlFor="is-fixo" className="text-lg font-semibold cursor-pointer">
                    Despesa fixa (aparece todo mês)
                  </Label>
                </div>
              )}
              {modalType === 'recorrente' && form.is_fixo && !editingItem && (
                <div className="space-y-3 rounded-xl bg-slate-50 border-2 border-slate-200 p-4">
                  <p className="text-sm text-slate-600">
                    Por padrão, começa em <span className="font-semibold">{getMonthName(currentMonth.mes)} de {currentMonth.ano}</span> e nos próximos meses.
                  </p>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="start-earlier"
                      checked={startEarlier}
                      onCheckedChange={(checked) => {
                        setStartEarlier(!!checked);
                        if (!checked) setStartMonth(null);
                      }}
                      className="w-7 h-7 border-2"
                      data-testid="start-earlier-checkbox"
                    />
                    <Label htmlFor="start-earlier" className="text-base font-semibold cursor-pointer">
                      Começar em um mês anterior
                    </Label>
                  </div>
                  {startEarlier && (
                    <Select
                      value={startMonth ? `${startMonth.mes}-${startMonth.ano}` : ''}
                      onValueChange={(v) => {
                        const [m, a] = v.split('-').map(Number);
                        setStartMonth({ mes: m, ano: a });
                      }}
                    >
                      <SelectTrigger className="h-12 text-base border-2" data-testid="start-month-select">
                        <SelectValue placeholder="Escolha o mês inicial" />
                      </SelectTrigger>
                      <SelectContent>
                        {prevMonthOptions.map((o) => (
                          <SelectItem key={`${o.mes}-${o.ano}`} value={`${o.mes}-${o.ano}`} className="text-base">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 pt-2">
                <Checkbox
                  id="pago"
                  checked={form.pago}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, pago: checked }))}
                  className="w-8 h-8 border-2"
                  data-testid="pago-checkbox"
                />
                <Label htmlFor="pago" className="text-lg font-semibold cursor-pointer">
                  Já foi pago
                </Label>
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="h-12 w-full sm:w-auto px-6 text-base border-2"
              >
                <X size={18} className="mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="h-12 w-full sm:w-auto px-6 text-base bg-[#1B2A4A] hover:bg-[#2C3E50]"
                data-testid="save-despesa-btn"
              >
                <Save size={18} className="mr-2" />
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scope dialog for fixed expense edits */}
        <AlertDialog open={!!scopeDialog} onOpenChange={() => setScopeDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-[#1B2A4A]">
                Alterar despesa fixa
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Esta é uma despesa fixa. Deseja alterar apenas este mês ou todos os meses?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-3">
              <AlertDialogCancel className="h-12 px-6 text-base border-2">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleScopeChoice(false)}
                className="h-12 px-6 text-base bg-slate-600 hover:bg-slate-700"
              >
                Só este mês
              </AlertDialogAction>
              <AlertDialogAction
                onClick={() => handleScopeChoice(true)}
                className="h-12 px-6 text-base bg-[#1B2A4A] hover:bg-[#2C3E50]"
              >
                Todos os meses
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-[#1B2A4A]">
                Tem certeza que deseja excluir?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3">
              <AlertDialogCancel className="h-12 px-6 text-base border-2">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="h-12 px-6 text-base bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

export default DespesasPage;
